import { h } from '@stencil/core';
import state from 'store';
import debugModalState, { resetDebugModal } from 'store/debugModal';
import { i18n } from 'i18n';
import { DebugDTO, ToolAndParamsDTO, EntityDTO, MemoryDTO } from 'client/types';
import { copyToClipboard } from 'utils/utils';

export default function DebugModal({ show }: { show: boolean }) {
  const onClose = () => {
    state.showFullScreenModal = null;
    resetDebugModal();
  };

  const toggleSection = (section: 'tools' | 'metadata' | 'attributes' | 'memories') => {
    debugModalState.expandedSections = {
      ...debugModalState.expandedSections,
      [section]: !debugModalState.expandedSections[section],
    };
  };

  const toggleTool = (index: number) => {
    const newExpandedTools = new Set(debugModalState.expandedTools);
    if (newExpandedTools.has(index)) {
      newExpandedTools.delete(index);
    } else {
      newExpandedTools.add(index);
    }
    debugModalState.expandedTools = newExpandedTools;
  };

  const toggleMemory = (index: number) => {
    const newExpandedMemories = new Set(debugModalState.expandedMemories);
    if (newExpandedMemories.has(index)) {
      newExpandedMemories.delete(index);
    } else {
      newExpandedMemories.add(index);
    }
    debugModalState.expandedMemories = newExpandedMemories;
  };

  const debugStr = (debug?: DebugDTO) => {
    if (!debug) {
      return '';
    }
    const tools = debug.tools?.map(t => ({ ...t, params: JSON.parse(t.params) }));
    return JSON.stringify(
      {
        tools: tools,
        metadata: debug.metadata,
        attributes: debug.attributes,
        memories: debug.memories,
      },
      null,
      2,
    );
  };

  const formatJSON = (jsonString: string) => {
    try {
      return JSON.stringify(JSON.parse(jsonString), null, 2);
    } catch {
      return jsonString;
    }
  };

  const parseEntity = (entityStr: string): EntityDTO | null => {
    try {
      return JSON.parse(entityStr) as EntityDTO;
    } catch {
      return null;
    }
  };

  const renderTools = (tools?: ToolAndParamsDTO[]) => {
    if (!tools || tools.length === 0) {
      return <div class="debug-empty-state">No tools executed</div>;
    }

    return (
      <div class="debug-tools-list">
        {tools.map((tool, index) => (
          <div class="debug-tool-item" key={index}>
            <div class="debug-tool-header" onClick={() => toggleTool(index)}>
              <div class="debug-tool-header-left">
                <i class={`eva-3-icon-chevron-${debugModalState.expandedTools.has(index) ? 'down' : 'right'} -eva-3-icon-sm`} />
                <span class="debug-tool-step">Step {tool.step}</span>
                <span class="debug-tool-name">{tool.tool}</span>
              </div>
            </div>
            {debugModalState.expandedTools.has(index) && (
              <div class="debug-tool-content">
                <div class="debug-tool-params">
                  <div class="debug-label">Parameters:</div>
                  <pre class="debug-code">{formatJSON(tool.params)}</pre>
                </div>
                {tool.entities && tool.entities.length > 0 && (
                  <div class="debug-tool-entities">
                    <div class="debug-label">Entities ({tool.entities.length}):</div>
                    <div class="debug-entities-list">
                      {tool.entities.map((entityStr, idx) => {
                        const entity = parseEntity(entityStr);
                        if (entity) {
                          return (
                            <div class="debug-entity-card" key={idx}>
                              <div class="debug-entity-header">
                                <span class="debug-entity-type">{entity.type}</span>
                                <span class="debug-entity-id">{entity.id}</span>
                              </div>
                              {entity.description && (
                                <div class="debug-entity-description">
                                  <i class="eva-3-icon-info-circle" />
                                  <span>{entity.description}</span>
                                </div>
                              )}
                              {entity.value && (
                                <div class="debug-entity-value">
                                  <pre class="debug-entity-value-content">{entity.value}</pre>
                                </div>
                              )}
                              {entity.version !== undefined && <div class="debug-entity-version">v{entity.version}</div>}
                            </div>
                          );
                        } else {
                          return (
                            <div class="debug-entity-card -raw" key={idx}>
                              <pre class="debug-entity-raw">{entityStr}</pre>
                            </div>
                          );
                        }
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    );
  };

  const renderMetadata = (metadata?: DebugDTO['metadata']) => {
    if (!metadata) {
      return <div class="debug-empty-state">No metadata available</div>;
    }

    return (
      <div class="debug-metadata-content">
        {metadata.conversation && (
          <div class="debug-metadata-section">
            <h5 class="debug-subtitle">Conversation</h5>
            <div class="debug-metadata-item">
              <span class="debug-label">Active:</span>
              <div class="debug-tag-list">
                {metadata.conversation.active?.map((item, idx) => (
                  <span class="debug-tag -active" key={idx}>
                    {item}
                  </span>
                ))}
              </div>
            </div>
            <div class="debug-metadata-item">
              <span class="debug-label">Enabled:</span>
              <div class="debug-tag-list">
                {metadata.conversation.enabled?.map((item, idx) => (
                  <span class="debug-tag -enabled" key={idx}>
                    {item}
                  </span>
                ))}
              </div>
            </div>
          </div>
        )}

        {metadata.prompts && (
          <div class="debug-metadata-section">
            <h5 class="debug-subtitle">Prompts</h5>
            {metadata.prompts.start && (
              <div class="debug-metadata-item">
                <span class="debug-label">Start:</span>
                <pre class="debug-code -compact">{metadata.prompts.start}</pre>
              </div>
            )}
            {metadata.prompts.followup && (
              <div class="debug-metadata-item">
                <span class="debug-label">Follow-up:</span>
                <pre class="debug-code -compact">{metadata.prompts.followup}</pre>
              </div>
            )}
            {metadata.prompts.compose && (
              <div class="debug-metadata-item">
                <span class="debug-label">Compose:</span>
                <pre class="debug-code -compact">{metadata.prompts.compose}</pre>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  const renderAttributes = (attributes?: Record<string, string>) => {
    if (!attributes || Object.keys(attributes).length === 0) {
      return <div class="debug-empty-state">No attributes available</div>;
    }

    return (
      <div class="debug-attributes-list">
        {Object.entries(attributes).map(([key, value], index) => (
          <div class="debug-attribute-item" key={index}>
            <span class="debug-attribute-key">{key}:</span>
            <span class="debug-attribute-value">{value}</span>
          </div>
        ))}
      </div>
    );
  };

  const formatMemoryValue = (value: MemoryDTO['value']) => {
    if (value.string) {
      return value.string;
    }
    if (value.map) {
      return JSON.stringify(value.map, null, 2);
    }
    if (value.attributes) {
      return JSON.stringify(value.attributes, null, 2);
    }
    return 'No value';
  };

  const renderMemories = (memories?: MemoryDTO[]) => {
    if (!memories || memories.length === 0) {
      return <div class="debug-empty-state">No memories available</div>;
    }

    return (
      <div class="debug-memories-list">
        {memories.map((memory, index) => (
          <div class="debug-memory-item" key={index}>
            <div class="debug-memory-header" onClick={() => toggleMemory(index)}>
              <div class="debug-memory-header-left">
                <i class={`eva-3-icon-chevron-${debugModalState.expandedMemories.has(index) ? 'down' : 'right'} -eva-3-icon-sm`} />
                <span class="debug-memory-id">{memory.id}</span>
                {memory.value.type && <span class="debug-memory-type">{memory.value.type}</span>}
              </div>
            </div>
            {debugModalState.expandedMemories.has(index) && (
              <div class="debug-memory-content">
                <div class="debug-memory-value">
                  <div class="debug-label">Value:</div>
                  <pre class="debug-code">{formatMemoryValue(memory.value)}</pre>
                </div>

                <div class="debug-memory-scope">
                  <div class="debug-label">Scope:</div>
                  <div class="debug-memory-scope-items">
                    {memory.config.scope.assistantId && (
                      <span class="debug-scope-item">
                        <i class="eva-3-icon-user" />
                        Assistant: {memory.config.scope.assistantId}
                      </span>
                    )}
                    {memory.config.scope.userId && (
                      <span class="debug-scope-item">
                        <i class="eva-3-icon-user" />
                        User: {memory.config.scope.userId}
                      </span>
                    )}
                    {memory.config.scope.chatId && (
                      <span class="debug-scope-item">
                        <i class="eva-3-icon-chat" />
                        Chat: {memory.config.scope.chatId}
                      </span>
                    )}
                    {memory.config.scope.tags && Object.keys(memory.config.scope.tags).length > 0 && (
                      <div class="debug-scope-tags">
                        <span class="debug-label-sm">Tags:</span>
                        {Object.entries(memory.config.scope.tags).map(([key, value], idx) => (
                          <span class="debug-tag -tag" key={idx}>
                            {key}: {value}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <div class="debug-memory-capabilities">
                  <div class="debug-label">Capabilities:</div>
                  <div class="debug-tag-list">
                    {memory.config.capabilities.map((cap, idx) => (
                      <span class="debug-tag -capability" key={idx}>
                        {cap}
                      </span>
                    ))}
                  </div>
                </div>

                <div class="debug-memory-timestamps">
                  <span class="debug-timestamp">
                    <i class="eva-3-icon-clock" />
                    Created: {new Date(memory.createdAt).toLocaleString()}
                  </span>
                  {memory.expireAt && (
                    <span class="debug-timestamp -expire">
                      <i class="eva-3-icon-clock" />
                      Expires: {new Date(memory.expireAt).toLocaleString()}
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    );
  };

  const debug = state.debug;

  return show ? (
    <div id="debug-modal" class="eva-3-modal-fullscreen -show-modal">
      <div class="modal-fullscreen-header">
        <i class="eva-3-icon-cross modal-fullscreen-close" role="button" tabindex="0" onClick={onClose} />
        <div class="modal-fullscreen-header-container">
          <h4 class="modal-fullscreen-title">
            <i class={`eva-3-icon-question-circle -eva-3-icon-sm -eva-3-mr-sm`} /> {i18n.t.chat.message.actions.debug}
          </h4>
          {debug?.troubleshooterUrl && (
            <a href={debug.troubleshooterUrl} target="_blank" rel="noopener noreferrer" class="debug-external-link">
              <i class="eva-3-icon-external-link" />
              <span>View Extended Debug</span>
            </a>
          )}
        </div>
      </div>
      <div class="modal-fullscreen-content">
        <div class="debug-modal-container">
          {/* Tools Section */}
          <div class="debug-section">
            <div class="debug-section-header" onClick={() => toggleSection('tools')}>
              <div class="debug-section-header-left">
                <i class={`eva-3-icon-chevron-${debugModalState.expandedSections.tools ? 'down' : 'right'} -eva-3-icon-sm`} />
                <h4 class="debug-section-title">Tools Executed</h4>
                {debug?.tools && <span class="debug-badge">{debug.tools.length}</span>}
              </div>
            </div>
            {debugModalState.expandedSections.tools && <div class="debug-section-content">{renderTools(debug?.tools)}</div>}
          </div>

          {/* Metadata Section */}
          <div class="debug-section">
            <div class="debug-section-header" onClick={() => toggleSection('metadata')}>
              <div class="debug-section-header-left">
                <i class={`eva-3-icon-chevron-${debugModalState.expandedSections.metadata ? 'down' : 'right'} -eva-3-icon-sm`} />
                <h4 class="debug-section-title">Flow Metadata</h4>
              </div>
            </div>
            {debugModalState.expandedSections.metadata && <div class="debug-section-content">{renderMetadata(debug?.metadata)}</div>}
          </div>

          {/* Attributes Section */}
          <div class="debug-section">
            <div class="debug-section-header" onClick={() => toggleSection('attributes')}>
              <div class="debug-section-header-left">
                <i class={`eva-3-icon-chevron-${debugModalState.expandedSections.attributes ? 'down' : 'right'} -eva-3-icon-sm`} />
                <h4 class="debug-section-title">Attributes</h4>
                {debug?.attributes && <span class="debug-badge">{Object.keys(debug.attributes).length}</span>}
              </div>
            </div>
            {debugModalState.expandedSections.attributes && <div class="debug-section-content">{renderAttributes(debug?.attributes)}</div>}
          </div>

          {/* Memories Section */}
          <div class="debug-section">
            <div class="debug-section-header" onClick={() => toggleSection('memories')}>
              <div class="debug-section-header-left">
                <i class={`eva-3-icon-chevron-${debugModalState.expandedSections.memories ? 'down' : 'right'} -eva-3-icon-sm`} />
                <h4 class="debug-section-title">Memories</h4>
                {debug?.memories && <span class="debug-badge">{debug.memories.length}</span>}
              </div>
            </div>
            {debugModalState.expandedSections.memories && <div class="debug-section-content">{renderMemories(debug?.memories)}</div>}
          </div>
        </div>

        {/* Action Buttons */}
        <div class="debug-actions">
          <button type="button" class="eva-3-btn -md -primary" onClick={() => copyToClipboard(debugStr(debug))}>
            <em class="btn-text">Copiar JSON</em>
          </button>
        </div>
      </div>
    </div>
  ) : null;
}
